import { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Badge } from '../ui/badge';
import { DollarSign, Cpu, Mic, Volume2, TrendingUp, ChevronDown } from 'lucide-react';
import { Button } from '../ui/button';
import { ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmt$(v) {
  if (v >= 1) return `$${v.toFixed(2)}`;
  if (v >= 0.01) return `$${v.toFixed(3)}`;
  return `$${v.toFixed(4)}`;
}

function fmtK(v) {
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000) return `${(v / 1_000).toFixed(1)}K`;
  return v.toString();
}

// Human-readable labels for the fine-grained usage contexts logged by the backend
const CONTEXT_LABELS = {
  clone_chat: 'Chat clone (utilisateur)',
  global_chat: 'Chat global (multi-clones)',
  query_rewrite: 'Reformulation de question',
  mapreduce_map: 'Résumé Map-Reduce (map)',
  mapreduce_reduce: 'Résumé Map-Reduce (reduce)',
  mapreduce_list_map: 'Extraction liste (map)',
  mapreduce_list_reduce: 'Extraction liste (reduce)',
  weekly_report_single: 'Rapport hebdo (single)',
  weekly_report_reduce: 'Rapport hebdo (Map-Reduce)',
  knowledge_extraction: 'Extraction de connaissances',
  behaviour_analysis: 'Analyse comportement clone',
  email_summary: "Résumé d'emails",
  call_summary: "Résumé d'appels",
  call_transcription: 'Transcription audio (Whisper)',
  whatsapp_conversation_summary: 'Résumé conversations WhatsApp',
  contact_enrichment: 'Enrichissement contacts',
  dossier_summary: 'Résumé de dossiers',
  privacy_classifier: 'Classifier privacy (1er message)',
  voice_chat_tts: 'Chat vocal (TTS)',
};

export function UsageDashboard({ token, days = 30 }) {
  const [overview, setOverview] = useState(null);
  const [byTenant, setByTenant] = useState(null);
  const [loading, setLoading] = useState(true);

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  useEffect(() => {
    async function fetch() {
      setLoading(true);
      try {
        const [ov, bt] = await Promise.all([
          axios.get(`${API}/platform/usage/overview?days=${days}`, { headers }),
          axios.get(`${API}/platform/usage/by-tenant?days=${days}`, { headers }),
        ]);
        setOverview(ov.data);
        setByTenant(bt.data);
      } catch (e) {
        console.error('Usage fetch error:', e);
      } finally {
        setLoading(false);
      }
    }
    fetch();
  }, [days, headers]);

  const t = overview?.totals || {};
  const daily = overview?.daily || [];
  const tenants = byTenant?.tenants || [];
  const byType = overview?.by_type || {};
  const byContext = overview?.by_context || [];
  const hasData = t.total_calls > 0;

  // Compute cumulative cost for trend line
  const chartData = useMemo(() => {
    let cumulative = 0;
    return daily.map(d => {
      cumulative += d.cost;
      return { ...d, cumulative: Math.round(cumulative * 100) / 100 };
    });
  }, [daily]);

  if (loading && !overview) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-sm text-muted-foreground">
          Chargement des données de consommation...
        </CardContent>
      </Card>
    );
  }

  const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const d = payload[0]?.payload;
    if (!d) return null;
    return (
      <div className="bg-popover border rounded-md shadow-md p-2.5 text-xs">
        <p className="font-medium mb-1">{d.date}</p>
        <p>Coût : {fmt$(d.cost)}</p>
        <p>Appels LLM : {d.calls}</p>
        <p className="text-muted-foreground mt-1">Cumulé : {fmt$(d.cumulative)}</p>
      </div>
    );
  };

  return (
    <div className="space-y-4" data-testid="usage-dashboard">
      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiCard icon={DollarSign} label="Coût estimé" value={fmt$(t.total_cost || 0)} color="text-emerald-500" bg="bg-emerald-500/10" tooltip="Estimation calculée à partir des tokens et prix publics OpenAI. Ta vraie facturation est visible dans Emergent → Universal Key → Usage." />
        <KpiCard icon={Cpu} label="Appels LLM" value={fmtK(t.total_calls || 0)} color="text-blue-500" bg="bg-blue-500/10" />
        <KpiCard icon={Mic} label="Transcriptions" value={`${Math.round(t.total_transcription_min || 0)} min`} color="text-amber-500" bg="bg-amber-500/10" />
        <KpiCard icon={Volume2} label="TTS" value={fmtK(t.total_tts_chars || 0)} sub="caractères" color="text-violet-500" bg="bg-violet-500/10" />
      </div>

      {/* By type breakdown */}
      {hasData && (
        <div className="flex flex-wrap gap-2">
          {Object.entries(byType).map(([type, data]) => (
            <Badge key={type} variant="outline" className="text-xs px-2 py-1">
              {type === 'chat' ? 'Chat' : type === 'transcription' ? 'Whisper' : 'TTS'}: {fmt$(data.cost)} ({data.count} appels)
            </Badge>
          ))}
        </div>
      )}

      {/* By context breakdown — fine-grained per-usage */}
      {hasData && byContext.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <Cpu className="w-4 h-4 text-blue-500" /> Consommation par usage
              <span className="ml-1 text-[10px] font-normal text-muted-foreground/70" title="Ventilation fine par type de tâche LLM: résumés WhatsApp, extraction de connaissances, chat clone, etc.">ⓘ</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-1.5" data-testid="usage-by-context">
              {byContext.slice(0, 12).map((c) => {
                const pct = t.total_cost > 0 ? Math.round((c.cost / t.total_cost) * 100) : 0;
                return (
                  <div key={c.context} className="flex items-center gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between text-xs mb-1">
                        <span className="font-medium truncate">{CONTEXT_LABELS[c.context] || c.context}</span>
                        <span className="text-muted-foreground">{fmt$(c.cost)} · {c.count} appels · {pct}%</span>
                      </div>
                      <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                        <div
                          className="h-full bg-blue-500 rounded-full"
                          style={{ width: `${Math.max(2, pct)}%` }}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
              {byContext.length > 12 && (
                <p className="text-[10px] text-muted-foreground pt-1">+ {byContext.length - 12} autres usages</p>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Daily chart */}
      {hasData && chartData.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-emerald-500" /> Dépenses quotidiennes
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div style={{ width: '100%', height: 180 }} data-testid="usage-daily-chart">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={chartData} margin={{ top: 4, right: 4, bottom: 0, left: -10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.3} vertical={false} />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
                    tickFormatter={v => v.slice(8)}
                    tickLine={false}
                    axisLine={{ stroke: 'hsl(var(--border))', opacity: 0.4 }}
                  />
                  <YAxis
                    tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={v => `$${v}`}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="cost" fill="hsl(var(--primary))" radius={[2, 2, 0, 0]} maxBarSize={24} opacity={0.7} />
                  <Line
                    type="monotone"
                    dataKey="cumulative"
                    stroke="#22c55e"
                    strokeWidth={2}
                    dot={false}
                    yAxisId={0}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}

      {/* By tenant table */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            <DollarSign className="w-4 h-4 text-emerald-500" /> Consommation par tenant
          </CardTitle>
        </CardHeader>
        <CardContent>
          {tenants.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center">
              Aucune donnée de consommation. Les appels LLM seront trackés automatiquement.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs">Tenant</TableHead>
                  <TableHead className="text-xs text-right">Chat</TableHead>
                  <TableHead className="text-xs text-right">Transcriptions</TableHead>
                  <TableHead className="text-xs text-right">TTS</TableHead>
                  <TableHead className="text-xs text-right">Tokens (in/out)</TableHead>
                  <TableHead className="text-xs text-right">Coût</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tenants.map((t) => (
                  <TableRow key={t.tenant_id} data-testid={`usage-tenant-${t.tenant_id}`}>
                    <TableCell className="text-sm font-medium">{t.tenant_name}</TableCell>
                    <TableCell className="text-xs text-right">{t.chat_calls}</TableCell>
                    <TableCell className="text-xs text-right">{t.transcriptions} ({t.transcription_min} min)</TableCell>
                    <TableCell className="text-xs text-right">{t.tts_calls}</TableCell>
                    <TableCell className="text-xs text-right text-muted-foreground">{fmtK(t.tokens_in)} / {fmtK(t.tokens_out)}</TableCell>
                    <TableCell className="text-sm text-right font-semibold">{fmt$(t.cost)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function KpiCard({ icon: Icon, label, value, sub, color, bg, tooltip }) {
  return (
    <Card title={tooltip}>
      <CardContent className="p-3">
        <div className="flex items-center gap-2 mb-1">
          <div className={`p-1.5 rounded-md ${bg}`}><Icon className={`w-3.5 h-3.5 ${color}`} /></div>
          <span className="text-[11px] text-muted-foreground">{label}{tooltip && <span className="ml-1 text-muted-foreground/60" title={tooltip}>ⓘ</span>}</span>
        </div>
        <p className="text-xl font-bold">{value}</p>
        {sub && <p className="text-[10px] text-muted-foreground">{sub}</p>}
      </CardContent>
    </Card>
  );
}
